import { useQuery, useQueries } from "react-query";
import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import MainCard from "components/layout/MainCard/MainCard";
import LoadingWrapper from "components/Loading/LoadingWrapper";
import useAPI from "hooks/useAPI";
import { GlobalQueries } from "hooks/useRefreshQuery";
import DataTable from "../../components/DataTable/DataTable";
import TableScrollHint from "../../components/Table/TableScrollHint";
import { currencyFormat, simpleFloat } from "../../helpers/functions";
import { externalFieldValues, externalPositionRows } from "../../helpers/orderExternalAttributes";
import ListBlock from "./ListBlock/ListBlock";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import Tooltip from "@mui/material/Tooltip";
import scss from "./OrdersDetailsPage.module.scss";
import columns from "./columns.json";
import DeleteDialog from "components/Dialogs/DeleteDielog";
import MediaPrint from "./MediaPrint/MediaPrint";
import { toast } from "react-toastify";
import PlusMinusInput from "../../components/Form/PlusMinusInput/PlusMinusInput";
import { useTranslation,  } from "react-i18next";

const OrdersDetailsPage = () => {
    const { t } = useTranslation();

    const api = useAPI();
    const { id } = useParams();
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [loading, setLoading] = useState(false);

    // Load all products in the cart
    const { data, isLoading } = useQuery([GlobalQueries.Orders], () => api.get(`/previous-orders/${id}`));
    const [showDialog, setShowDialog] = useState(false);
    const rabateCalc = (item) => {
        let rabate = [];

        item?.price_campaigns.map((price_campaign) => {
            if (price_campaign?.campaign_type === "b2b_rebate") {
                rabate.push(price_campaign?.calc_name);
            }
            return null;
        });

        return rabate.join("; ");
    };

    // Show the products in the table
    let products = (data?.items ?? []).map((item) => {
        const id_product = item?.basic_data?.id_product;
        const name = item?.basic_data?.name + (item?.basic_data?.attributes_text ? " (" + item?.basic_data?.attributes_text + ")" : "");
        const code = item?.basic_data?.sku;
        const linkStyle = { color: "inherit", textDecoration: "none" };

        return {
            image: item?.basic_data?.image ? <img src={item?.basic_data?.image} style={{ maxHeight: "30px" }} alt="" /> : null,
            code: id_product ? (
                <Link to={`/katalog/artikal/${id_product}`} style={linkStyle}>
                    {code}
                </Link>
            ) : (
                code
            ),
            name: id_product ? (
                <Link to={`/katalog/artikal/${id_product}`} style={linkStyle}>
                    {name}
                </Link>
            ) : (
                name
            ),
            slug: item?.basic_data?.slug,

            id: id_product,
            priceWithOutVat: currencyFormat(item?.price?.price_with_out_vat, item?.price?.currency),
            rebate: rabateCalc(item),
            discount: "-" + currencyFormat(item?.price?.price_discount_amount, item?.price?.currency),
            price: currencyFormat(item?.price?.price_subtotal, item?.price?.currency),
            quantity: simpleFloat(item?.price?.quantity),
            total: currencyFormat(item?.price?.subtotal),
            // Item external attributes, shown when arranged as columns in the CMS (items_table_columns)
            ...externalFieldValues(item?.external_attributes?.order_details_items_list),
        };
    });

    const convertDate = (date) => {
        const options = {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            hour12: false,
        };

        const dateObj = new Date(date);
        const formattedDate = dateObj
            .toLocaleString("sr-RS", options)
            .replace(/,/g, "")
            .replace(/(\d+)\D+(\d+)\D+(\d+)\D+(\d+):(\d+)/, "$1.$2.$3. $4:$5");
        return formattedDate;
    };

    const handlePrint = () => {
        window.print();
    };
    const { data: duplicatedProducts } = useQuery([data?.order?.order_token], () => api.get(`/previous-orders/duplicate/message/${data?.order?.order_token}`));

    const allowDuplicate = duplicatedProducts?.allow_duplicate;
    const message = duplicatedProducts?.message;
    const getAllProducts = (products, duplicatedProducts) => {
        products?.map((item) => {
            if (item?.allow_add) {
                setSelectedProducts((prev) => [...prev, item?.cart]);
            }
        });
    };

    // Učitavanje osnovnih podataka za sve proizvode
    const productQueries = useQueries(
        (duplicatedProducts?.items || []).map((item) => ({
            queryKey: [`Product:${item.product.id}:Basic`],
            queryFn: () => api.get(`/product-details/basic-data/${item.product.id}`).then((res) => res),
            enabled: !!duplicatedProducts,
        }))
    );

    // Extra saved delivery info (e.g. selected store, courier/pickup details) - only meaningful
    // when the provider actually populated it, unlike format_data.full_name which just repeats
    // delivery_method_name shown above it.
    const deliveryAdditionalText = data?.deliveries?.[0]?.format_data?.partial?.additional
        ?.map((row) => row.text)
        .filter(Boolean)
        .join(", ");

    const orderSummaryRows = data
        ? [
              ...externalPositionRows(data?.external_attributes?.order_details_header),
              ...[
                  ["order_details-default-order_list-created_at", convertDate(data.order.created_at)],
                  ["order_details-default-order_list-company_name", data.billing_address?.company_name],
                  ["order_details-default-order_list-address", data.billing_address?.address],
                  ["order_details-default-order_list-town_name", data.billing_address?.town_name],
                  ["order_details-default-order_list-zip_code", data.billing_address?.zip_code],
                  ["order_details-default-order_list-country_name", data.billing_address?.country_name],
                  ["order_details-default-order_list-delivery_method_name", data.order.delivery_method_name],
                  ["order_details-default-order_list-social_security_number", data.billing_address?.maticni_broj],
                  ["order_details-default-order_list-orderer_of_goods", [data.billing_address?.first_name, data.billing_address?.last_name].filter(Boolean).join(" ")],
                  ["order_details-default-order_list-payment_method_name", data.order.payment_method_name],
                  ["order_details-default-order_list-pib", data.billing_address?.pib],
                  ["order_details-default-order_list-delivery_additional_info", deliveryAdditionalText],
                  ["order_details-default-note", data.order.note],
              ].filter(([, value]) => value != null && value !== ""),
          ]
        : [];

    const paymentSummaryRows = data
        ? [
              ["order_details-default-payment_list-total_with_out_vat", currencyFormat(data.order.total_with_out_vat, data.order.currency)],
              ["order_details-default-payment_list-total_rebate_amount", "-" + currencyFormat(data.order.total_rebate_amount, data.order.currency)],
              ["order_details-default-payment_list-total_discount_amount", "-" + currencyFormat(Number(data.order.total_items_discount_amount) + Number(data.order.total_cart_discount_amount), data.order.currency)],
              ...(Number(data.order.total_promo_code_amount) > 0
                  ? [["order_details-default-payment_list-promo_code", "-" + currencyFormat(data.order.total_promo_code_amount, data.order.currency)]]
                  : []),
              ["order_details-default-payment_list-subtotal", currencyFormat(data.order.subtotal, data.order.currency)],
              ["order_details-default-payment_list-total_vat", currencyFormat(data.order.total_vat, data.order.currency)],
              ["order_details-default-payment_list-total_delivery_amount", currencyFormat(data.order.total_delivery_amount, data.order.currency)],
              ["order_details-default-payment_list-total", currencyFormat(data.order.total, data.order.currency)],
          ]
        : [];

    const summaryLabel = (label) => `${String(t(label)).replace(/:\s*$/, "")}:`;

    return (
        <>
            <MainCard className="p">
                <div className="">
                    <div
                        className="page-content"
                        style={{
                            "@media print": {
                                padding: "0 !important",
                            },
                        }}
                    >
                        <div className={scss.headingRow}>
                            <div className={scss.headingTitle}>
                                <Link to="/kupovine" className={scss.backButton} aria-label={t("order_details-default-button-back_to_orders", "Nazad na kupovine")}>
                                    <ArrowBackRoundedIcon fontSize="small" aria-hidden="true" />
                                </Link>
                                <h4 className="h-underline">
                                    {t("order_details-default-title")}: {data?.order?.slug}
                                </h4>
                            </div>
                            {data && (
                                <div className={scss.headingActions}>
                                    <Link to="/kupovine" className={`${scss.backButton} ${scss.mobileBackButton}`} aria-label={t("order_details-default-button-back_to_orders", "Nazad na kupovine")}>
                                        <ArrowBackRoundedIcon fontSize="small" aria-hidden="true" />
                                    </Link>
                                    <Tooltip arrow title={allowDuplicate ? t("order_details-default-messages-select_products_for_cart") : message || ""} placement="top">
                                        <span>
                                            <button
                                                type="button"
                                                className={`${scss.actionButton} ${scss.addAction}`}
                                                disabled={!allowDuplicate}
                                                onClick={() => {
                                                    setShowDialog(true);
                                                    getAllProducts(duplicatedProducts?.items, duplicatedProducts);
                                                }}
                                            >
                                                <ReplayRoundedIcon fontSize="small" aria-hidden="true" />
                                                <span>{t("order_details-default-button-repeat_purchase", "Ponovi kupovinu")}</span>
                                            </button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip arrow title={t("order_details-default-messages-printing_document")} placement="top">
                                        <button type="button" className={`${scss.actionButton} ${scss.printAction}`} onClick={handlePrint}>
                                            <PrintOutlinedIcon fontSize="small" aria-hidden="true" />
                                            <span>{t("order_details-default-button-print")}</span>
                                        </button>
                                    </Tooltip>
                                </div>
                            )}
                        </div>

                        <MediaPrint data={data} />

                        <LoadingWrapper isLoading={isLoading}>
                            {data && (
                                <div className={scss.wrapper}>
                                    <div className={scss.itemsList}>
                                        <div className={scss.tablePrintOverflow} style={{ maxHeight: "100%", overflow: "hidden" }}>
                                            <TableScrollHint />
                                            <DataTable actions={{ previewUrl: "/katalog/artikal/" }} columns={data?.items_table_columns?.length > 0 ? data.items_table_columns : columns} data={products} maxHeight={"40vh"} />
                                        </div>
                                    </div>

                                    <section className={scss.orderSummary} aria-label={t("order_details-default-title")}>
                                        <dl className={scss.summaryDetails}>
                                            {orderSummaryRows.map(([label, value], index) => (
                                                <div className={scss.summaryDetail} key={`${label}-${index}`}>
                                                    <dt>{summaryLabel(label)}</dt>
                                                    <dd>{value}</dd>
                                                </div>
                                            ))}
                                        </dl>
                                        <dl className={scss.summaryTotals}>
                                            {paymentSummaryRows.map(([label, value], index) => (
                                                <div className={`${scss.summaryTotal} ${index === paymentSummaryRows.length - 1 ? scss.grandTotal : ""}`} key={label}>
                                                    <dt>{summaryLabel(label)}</dt>
                                                    <dd>{value}</dd>
                                                </div>
                                            ))}
                                        </dl>
                                    </section>

                                    {data?.order?.note && <ListBlock title={"order_details-default-note"} list={[[null, data.order.note]]} additionalClass={scss.notePrint} />}
                                    {data?.payments?.[0]?.data?.status_info?.fields?.some((field) => field.value != null && field.value !== "") && (
                                        <ListBlock
                                            additionalClass={`${scss.orderInfo} ${scss.paymentStatusInfo}`}
                                            title={"Status plaćanja"}
                                            list={data.payments[0].data.status_info.fields.map((field) => [field.label, field.value])}
                                        />
                                    )}

                                    {data?.external_attributes?.order_details_external?.attributes?.length > 0 && (
                                        <ListBlock
                                            additionalClass={scss.orderInfo}
                                            title={data.external_attributes.order_details_external.name}
                                            list={externalPositionRows(data.external_attributes.order_details_external)}
                                        />
                                    )}

                                    {[
                                        data?.packages?.package_count,
                                        data?.packages?.total_quantity,
                                        data?.packages?.total_weight,
                                        data?.packages?.total_gross_weight,
                                        data?.packages?.action_packaging_profile_name,
                                    ].some((value) => value != null && value !== "") && (
                                        <ListBlock
                                            additionalClass={`${scss.orderInfo} ${scss.packagingInfo}`}
                                            title={"Podaci o pakovanju"}
                                            list={[
                                                ["Broj paketa", data.packages.package_count],
                                                ["Ukupna količina", data.packages.total_quantity],
                                                ["Ukupna težina (kg)", data.packages.total_weight],
                                                ["Bruto težina (kg)", data.packages.total_gross_weight],
                                                ["Tip pakovanja", data.packages.action_packaging_profile_name],
                                            ]}
                                        />
                                    )}
                                </div>
                            )}
                        </LoadingWrapper>
                    </div>
                </div>
                <DeleteDialog
                    purchase
                    loadingwidth={scss.loadingwidth}
                    openDeleteDialog={{ show: showDialog }}
                    selectedProducts={selectedProducts}
                    loading={loading}
                    getAllProducts={getAllProducts}
                    products={duplicatedProducts?.items}
                    setOpenDeleteDialog={() => setShowDialog(false)}
                    handleConfirm={async () => {
                        const checkQuantityNullValues = selectedProducts?.filter((product) => product?.quantity === null);

                        if (selectedProducts?.length > 0 && checkQuantityNullValues?.length === 0) {
                            setLoading(true);
                            await api.put(`/previous-orders/duplicate/confirm`, { cart_items: selectedProducts }).then((res) => {
                                res?.add_items?.length > 0 &&
                                    toast.success(t("order_details-default-messages-success_added_product", { count: res?.add_items?.length }));

                                res?.error_items?.length > 0 &&
                                    res?.error_items?.forEach((item) => {
                                        toast.success(item?.message);
                                    });

                                setShowDialog(false);

                                setSelectedProducts(
                                    duplicatedProducts?.items?.map((item) => {
                                        const isAddable = item?.allow_add;
                                        if (isAddable) {
                                            return item?.cart;
                                        }
                                    })
                                );
                                setTimeout(() => {
                                    setLoading(false);
                                }, 500);
                            });
                        } else {
                            if (checkQuantityNullValues?.length > 0) {
                                toast.warn("Niste uneli količinu za sve proizvode.");
                            } else {
                                toast.error("Niste izabrali nijedan proizvod.");
                            }
                        }
                    }}
                    title={"order_details-default-delete_dialog-title"}
                    description={"order_details-default-delete_dialog-description"}
                    nameOfButtonDelete={"order_details-default-delete_dialog-button"}
                    deafultIcon={false}
                    setSelectedProducts={setSelectedProducts}
                >
                    {duplicatedProducts?.items?.map((item, index) => {
                        const isAddable = item?.allow_add;
                        const isAdded = selectedProducts?.find((product) => product?.id_product === item?.cart?.id_product);

                        const basicData = productQueries[index]?.data;
                        let behaviours;
                        let inventory;
                        if (basicData?.data?.variant_items.length > 0) {
                            const currentVariant = basicData?.data?.variant_items.find((variantItem) => variantItem.id == item.product.id);
                            // The ordered variant may no longer be among the product's variants (removed or deactivated)
                            behaviours = currentVariant?.behaviours;
                            inventory = currentVariant?.inventory;
                        } else {
                            behaviours = basicData?.data?.item?.behaviours;
                            inventory = basicData?.data?.item?.inventory;
                        }

                        return (
                            <div key={index}>
                                {isAddable && (
                                    <div className={`${scss.modalproduct} ${isAdded ? scss.added : scss.notAdded}`}>
                                        <button
                                            type="button"
                                            className={scss.productChoice}
                                            aria-pressed={Boolean(isAdded)}
                                            disabled={loading}
                                            onClick={(e) => {
                                                if (!isAdded) {
                                                    setSelectedProducts([...selectedProducts, item?.cart]);
                                                } else {
                                                    setSelectedProducts(selectedProducts.filter((product) => product?.id_product !== item?.cart?.id_product));
                                                }
                                            }}
                                        >
                                            <span className={scss.selectionMark} aria-hidden="true">{isAdded && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 4 4L19 6" /></svg>}</span>
                                            <img src={item?.product?.image} alt="" width={50} height={50} />
                                            <span className={scss.productLabel}>{item?.product?.name}<small>Šifra: {item?.product?.sku}</small></span>
                                        </button>
                                        <PlusMinusInput
                                            className={scss.quantity}
                                            behaviours={behaviours?.cart}
                                            max={Number(inventory?.amount)}
                                            quantity={Number(item?.cart?.quantity)}
                                            setQuantity={(quantity) => {
                                                setSelectedProducts(
                                                    selectedProducts.map((product) => {
                                                        if (product?.id_product === item?.cart?.id_product) {
                                                            product.quantity = quantity;
                                                        }
                                                        return product;
                                                    })
                                                );
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </DeleteDialog>
            </MainCard>
        </>
    );
};

export default OrdersDetailsPage;
